<template lang="pug">
.inside-padding
  form(@submit.prevent="openConfirmation")
    h3.capitalize Factura individual

    .input-container
      input.input(v-model="form.dni" required :disabled="isSending" @blur="searchCustomer" @keydown.enter.prevent="searchCustomer")
      label.label(:class="{ show: form.dni }") Número de documento (sin dígito de verificación)

    p.individual-invoice-help(v-if="customerMessage" :class="customerMessageClass") {{ customerMessage }}

    .input-container.arrow
      select.select(v-model="form.personType" required :disabled="isSending")
        option(value="natural") Natural
        option(value="juridica") Jurídica
      label.label.show Tipo de persona

    .input-container.arrow
      select.select(v-model="form.documentType" required :disabled="isSending")
        option(v-for="type in documentTypes" :key="type.value" :value="type.value") {{ type.label }}
      label.label.show Tipo de documento

    .input-container
      input.input(v-model="form.name" required :disabled="isSending")
      label.label(:class="{ show: form.name }") Nombre o razón social

    .input-container
      input.input(v-model="form.email" type="email" required :disabled="isSending")
      label.label(:class="{ show: form.email }") Correo

    .input-container
      input.input(v-model="form.telephone" required :disabled="isSending")
      label.label(:class="{ show: form.telephone }") Teléfono

    .input-container
      input.input(v-model="form.address" required :disabled="isSending")
      label.label(:class="{ show: form.address }") Dirección

    .input-container
      input.input(:value="locationSearch" required :disabled="isSending" @input="onLocationInput" autocomplete="ño")
      label.label(:class="{ show: locationSearch }") Ciudad

    .individual-invoice-locations(v-if="locationsFiltered.length && !form.location")
      button.individual-invoice-location(
        v-for="location in locationsFiltered"
        :key="`${location.location}-${location.department}`"
        type="button"
        @click="chooseLocation(location)"
      ) {{ location.location }}, {{ location.department }}

    h3.capitalize.individual-invoice-subtitle Ítems
    p.individual-invoice-help El valor unitario incluye IVA (19%).

    .individual-invoice-item(v-for="(item, index) in form.items" :key="index")
      .input-container.description
        input.input(v-model="item.description" required :disabled="isSending")
        label.label(:class="{ show: item.description }") Descripción
      .input-container.code
        input.input(v-model="item.code" required maxlength="20" :disabled="isSending")
        label.label(:class="{ show: item.code }") Código
      .input-container.quantity
        input.input(v-model.number="item.quantity" type="number" min="1" step="1" required :disabled="isSending")
        label.label.show Cantidad
      .input-container.price
        input.input(v-model.number="item.price" type="number" min="1" step="1" required :disabled="isSending")
        label.label(:class="{ show: item.price }") Valor unitario con IVA
      .individual-invoice-item-total {{ formatMoney(itemTotal(item)) }}
      button.individual-invoice-remove(v-if="form.items.length > 1" type="button" :disabled="isSending" @click="form.items.splice(index, 1)") Quitar

    button.individual-invoice-add(type="button" :disabled="isSending" @click="addItem") Agregar ítem

    .individual-invoice-totals
      div
        span Base
        span {{ formatMoney(totals.base) }}
      div
        span IVA
        span {{ formatMoney(totals.tax) }}
      div.total
        span Total
        span {{ formatMoney(totals.total) }}

    .input-container.arrow
      select.select(v-model="form.sendEmail" :disabled="isSending")
        option(:value="true") Sí
        option(:value="false") No
      label.label.show Enviar factura por correo

    button.individual-invoice-button(type="submit" :disabled="isSending")
      span {{ isSending ? 'Facturando...' : 'Crear factura' }}

    .individual-invoice-result(v-if="result" :class="result.type")
      | {{ result.message }}
      .individual-invoice-detail(v-if="result.detail") {{ result.detail }}
      a.individual-invoice-link(v-if="result.pdf" :href="result.pdf" target="_blank" rel="noopener") Ver PDF

  .container-individual-invoice-confirmation.center(v-if="showConfirmation")
    .container-message
      h3 Confirmación
      .text
        p Se emitirá ante la DIAN una factura a {{ form.name }} ({{ form.dni }}) por {{ formatMoney(totals.total) }}.
        p(v-if="form.sendEmail") Se enviará al correo {{ form.email }}.
        p(v-else) No se enviará correo al cliente.
      .buttons
        button.individual-invoice-modal-button(type="button" @click="showConfirmation = false") Cancelar
        button.individual-invoice-modal-button.confirm(type="button" @click="confirmCreate") Facturar
</template>

<script>
import gql from '@my/graphql'
import gqlInvoice from '@/graphql/invoice'
import gqlLocation from '@/graphql/location'

const IVA = 0.19

const emptyItem = () => ({ description: '', code: '', quantity: 1, price: null })

const emptyForm = () => ({
  dni: '',
  personType: 'natural',
  documentType: 'cc',
  name: '',
  email: '',
  telephone: '',
  address: '',
  location: '',
  department: '',
  sendEmail: true,
  items: [emptyItem()]
})

export default {
  name: 'IndividualInvoice',

  data () {
    return {
      form: emptyForm(),
      documentTypes: [
        { value: 'cc', label: 'Cédula' },
        { value: 'nit', label: 'NIT' },
        { value: 'ce', label: 'Cédula de extranjería' },
        { value: 'ti', label: 'Tarjeta de identidad' },
        { value: 'pc', label: 'Pasaporte' },
        { value: 'de', label: 'Documento extranjero' }
      ],
      searchedDni: '',
      customerMessage: '',
      customerMessageClass: '',
      locationSearch: '',
      locationsFiltered: [],
      locationTimeoutId: null,
      isSending: false,
      showConfirmation: false,
      result: null
    }
  },

  computed: {
    totals () {
      const total = this.form.items.reduce((acc, item) => acc + this.itemTotal(item), 0)
      const base = total / (1 + IVA)
      return { base, tax: total - base, total }
    }
  },

  methods: {
    itemTotal (item) {
      const quantity = Number(item.quantity) || 0
      const price = Number(item.price) || 0
      return quantity * price
    },

    formatMoney (value) {
      return '$' + Number(value || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 })
    },

    addItem () {
      this.form.items.push(emptyItem())
    },

    async searchCustomer () {
      const dni = this.form.dni.replace(/[\s.,-]/g, '').trim()
      if (!dni || dni === this.searchedDni) return

      this.searchedDni = dni
      this.customerMessageClass = ''
      this.customerMessage = 'Buscando cliente...'

      const response = await gql.query(gqlInvoice.GET_CUSTOMER, { dni })
      if (dni !== this.searchedDni) return

      const customer = response && response.data && response.data.getInvoiceCustomer

      if (!customer) {
        this.customerMessageClass = 'warning'
        this.customerMessage = 'No hay órdenes del último año con este documento. Ingresa los datos del cliente.'
        return
      }

      this.form.name = customer.name || ''
      this.form.email = customer.email || ''
      this.form.telephone = customer.telephone || ''
      this.form.address = customer.address || ''
      this.form.location = customer.location || ''
      this.form.department = customer.department || ''
      this.locationSearch = customer.location ? `${customer.location}, ${customer.department}` : ''
      this.locationsFiltered = []

      const date = customer.orderDate ? new Date(customer.orderDate).toLocaleDateString('es-CO') : ''
      this.customerMessageClass = 'success'
      this.customerMessage = `Datos traídos de la orden ${customer.order}${date ? ` del ${date}` : ''}. Revísalos antes de facturar.`
    },

    onLocationInput (event) {
      this.locationSearch = event.target.value
      this.form.location = ''
      this.form.department = ''

      if (this.locationTimeoutId) clearTimeout(this.locationTimeoutId)
      this.locationTimeoutId = setTimeout(this.fetchLocations, 300)
    },

    async fetchLocations () {
      const find = this.locationSearch
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9 ]/g, '')
        .trim()

      if (!find) {
        this.locationsFiltered = []
        return
      }

      const response = await gql.query(gqlLocation.GET_LOCATIONS_ECOMMERCE_FILTERED, { find })
      this.locationsFiltered = (response && response.data && response.data.getLocationsEcommerceFiltered) || []
    },

    chooseLocation (location) {
      this.form.location = location.location
      this.form.department = location.department
      this.locationSearch = `${location.location}, ${location.department}`
      this.locationsFiltered = []
    },

    openConfirmation () {
      if (this.isSending) return
      this.result = null

      if (!this.form.location) {
        this.result = { type: 'error', message: '✕ Elige la ciudad de la lista' }
        return
      }

      const invalidItem = this.form.items.findIndex(item => !Number.isInteger(item.quantity) || item.quantity < 1 || !Number.isInteger(item.price) || item.price < 1)
      if (invalidItem >= 0) {
        this.result = { type: 'error', message: `✕ Revisa la cantidad y el valor del ítem ${invalidItem + 1} (números enteros mayores a cero)` }
        return
      }

      this.showConfirmation = true
    },

    async confirmCreate () {
      this.showConfirmation = false
      this.isSending = true

      const input = {
        personType: this.form.personType,
        documentType: this.form.documentType,
        dni: this.form.dni,
        name: this.form.name,
        email: this.form.email,
        telephone: this.form.telephone,
        address: this.form.address,
        location: this.form.location,
        department: this.form.department,
        sendEmail: this.form.sendEmail,
        items: this.form.items.map(item => ({
          description: item.description,
          code: item.code,
          quantity: item.quantity,
          price: item.price
        }))
      }

      const response = await gql.mutate(gqlInvoice.CREATE_INDIVIDUAL, { input }, false)
      this.isSending = false

      const invoice = response && response.data && response.data.createInvoiceIndividual

      if (!invoice) {
        this.result = {
          type: 'error',
          message: '✕ No fue posible crear la factura',
          detail: (response && response.error) || 'No hubo respuesta del servidor'
        }
        return
      }

      this.result = {
        type: 'success',
        message: `✓ Factura ${invoice.document} creada por ${this.formatMoney(invoice.value)}`,
        detail: this.form.sendEmail ? `Se envió al correo ${this.form.email}.` : 'No se envió correo al cliente.',
        pdf: invoice.PDF
      }

      this.form = emptyForm()
      this.searchedDni = ''
      this.customerMessage = ''
      this.locationSearch = ''
      this.locationsFiltered = []
    }
  }
}
</script>

<style lang="stylus">
@import '~@/stylus/functions.styl'

.inside-padding
  padding-bottom 2em

.individual-invoice-help
  margin 0 1em 1em 1em
  font-size 1rem
  color rgb(80, 80, 80)
  text-align left

.individual-invoice-help.success
  color #1b7f3a

.individual-invoice-help.warning
  color #b54708

.individual-invoice-subtitle
  margin-top 1.5em

.individual-invoice-locations
  display flex
  flex-wrap wrap
  gap .5em
  margin 0 1em 1em 1em

.individual-invoice-location
  padding .6em 1em
  borderRadius(.3rem)
  border 1px solid rgba(0, 0, 0, .2)
  background transparent
  cursor pointer
  text-transform capitalize

.individual-invoice-item
  display flex
  flex-wrap wrap
  align-items center
  gap 0 .5em
  border-bottom 1px solid rgba(0, 0, 0, .1)
  margin-bottom 1em

.individual-invoice-item .description
  flex 3 1 16em

.individual-invoice-item .code
  flex 1 1 7em

.individual-invoice-item .quantity
  flex 1 1 6em

.individual-invoice-item .price
  flex 2 1 10em

.individual-invoice-item-total
  flex 1 1 8em
  font-weight bold
  text-align right
  margin 0 1em 1em 0

.individual-invoice-remove,
.individual-invoice-add
  padding .6em 1.2em
  borderRadius(.3rem)
  border 1px solid rgba(0, 0, 0, .3)
  background transparent
  cursor pointer
  margin 0 1em 1em 1em

.individual-invoice-totals
  margin 1em
  max-width 24em
  margin-left auto
  font-size 1.1em

.individual-invoice-totals div
  display flex
  justify-content space-between
  padding .3em 0

.individual-invoice-totals .total
  font-weight bold
  border-top 1px solid rgba(0, 0, 0, .2)

.individual-invoice-button
  display inline-flex
  justify-content center
  align-items center
  padding 1.3em 2em
  borderRadius(.3rem)
  cursor pointer
  noSelect()
  background-color var(--color-principal)
  border none
  color white
  font-size 1rem
  text-transform uppercase
  margin-left 1em

.individual-invoice-button:disabled
  opacity .6
  cursor default

.individual-invoice-result
  margin 1.5em 1em 0 1em
  font-size 1.1em
  text-align left

.individual-invoice-result.success
  color #1b7f3a

.individual-invoice-result.error
  color #b42318

.individual-invoice-detail
  margin-top .5em
  font-size .95em
  color rgb(80, 80, 80)

.individual-invoice-link
  display inline-block
  margin-top .5em

.container-individual-invoice-confirmation
  position fixed
  width 100%
  height 100%
  left 0
  top 0
  background-color rgba(0, 0, 0, .4)
  z-index 12000
  display flex
  justify-content center
  align-items center

.container-individual-invoice-confirmation .container-message
  background-color var(--background-principal)
  padding 2em 3em
  borderRadius(.5em)
  width 100%
  max-width 40em

.container-individual-invoice-confirmation .text
  margin .5em 0 1em 0
  font-size 1.1em

.container-individual-invoice-confirmation .text p
  margin .5em 0

.container-individual-invoice-confirmation .buttons
  display flex
  justify-content center
  align-items center
  gap 1em

.individual-invoice-modal-button
  display inline-flex
  justify-content center
  align-items center
  padding 1em 1.6em
  borderRadius(.3rem)
  cursor pointer
  noSelect()
  background transparent
  border 1px solid rgba(0, 0, 0, .3)
  font-size 1rem
  text-transform uppercase

.individual-invoice-modal-button.confirm
  background-color var(--color-principal)
  border none
  color white
</style>



// WEBPACK FOOTER //
// src/components/pages/invoice/IndividualInvoice.vue